import React from 'react'
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer'
import { RBC_BRANDING } from '../lib/proposalDocumentTemplates'

export interface StatementInvoiceRow {
  invoiceNumber: string
  invoiceDate?: string | null
  dueDate?: string | null
  total: number
  amountPaid: number
  balanceDue: number
}

export interface StatementPaymentRow {
  paymentDate: string
  invoiceNumber: string
  method: string
  amount: number
}

export interface CustomerStatementProps {
  customerName: string
  asOfDate: string
  invoices: StatementInvoiceRow[]
  payments: StatementPaymentRow[]
  totalBalanceDue: number
}

const BRAND_BLUE = '#168EB8'
const INK = '#1a1a1a'
const MUTED = '#555555'
const RULE = '#d6dde5'
const LOGO_URL = '/rbc-logo.png'

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingHorizontal: 46,
    paddingBottom: 60,
    fontFamily: 'Times-Roman',
    fontSize: 10,
    color: INK,
    lineHeight: 1.4,
  },
  letterhead: {
    borderBottomWidth: 2,
    borderBottomColor: BRAND_BLUE,
    paddingBottom: 10,
    marginBottom: 18,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  logo: { width: 150, height: 40 },
  letterheadAddr: { fontSize: 8.5, color: MUTED, textAlign: 'right' },
  title: { fontSize: 20, fontFamily: 'Times-Bold', color: BRAND_BLUE },
  subtitle: { fontSize: 11, color: MUTED, marginTop: 2 },
  metaRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 18 },
  metaLabel: { fontSize: 8.5, color: MUTED, marginBottom: 2 },
  metaValue: { fontSize: 10.5, fontFamily: 'Times-Bold' },
  sectionTitle: { fontSize: 12, fontFamily: 'Times-Bold', color: BRAND_BLUE, marginBottom: 6, marginTop: 18 },
  table: { borderWidth: 1, borderColor: RULE, marginTop: 6 },
  tr: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: RULE },
  trLast: { flexDirection: 'row' },
  th: { backgroundColor: '#f1f5f9', fontFamily: 'Times-Bold', fontSize: 9.5, padding: 5 },
  td: { fontSize: 9.5, padding: 5 },
  cellRight: { textAlign: 'right' },
  cellBorderLeft: { borderLeftWidth: 1, borderLeftColor: RULE },
  totalsBlock: { marginTop: 12, alignItems: 'flex-end' },
  grandTotalRow: {
    flexDirection: 'row',
    width: 240,
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: INK,
    paddingTop: 4,
    marginTop: 4,
  },
  totalsLabel: { fontSize: 10, color: MUTED },
  totalsValue: { fontSize: 10, fontFamily: 'Times-Bold' },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 46,
    right: 46,
    textAlign: 'center',
    fontSize: 8,
    color: '#999999',
  },
})

function money(n: number): string {
  return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatDate(date?: string | null): string {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export const CustomerStatementPdf: React.FC<CustomerStatementProps> = ({
  customerName,
  asOfDate,
  invoices,
  payments,
  totalBalanceDue,
}) => {
  return (
    <Document title={`Statement - ${customerName}`} author={RBC_BRANDING.companyName}>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.letterhead}>
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <Image style={styles.logo} src={LOGO_URL} />
          <Text style={styles.letterheadAddr}>
            {RBC_BRANDING.companyAddressLines.join('\n')}
            {'\n'}
            {RBC_BRANDING.repPhone}
          </Text>
        </View>

        <View style={styles.metaRow}>
          <View>
            <Text style={styles.title}>STATEMENT</Text>
            <Text style={styles.subtitle}>As of {formatDate(asOfDate)}</Text>
          </View>
          <View>
            <Text style={styles.metaLabel}>Customer</Text>
            <Text style={styles.metaValue}>{customerName}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Open Invoices</Text>
        <View style={styles.table}>
          <View style={styles.tr}>
            <Text style={[styles.th, { flex: 2 }]}>Invoice #</Text>
            <Text style={[styles.th, styles.cellBorderLeft, { flex: 1.3 }]}>Date</Text>
            <Text style={[styles.th, styles.cellBorderLeft, { flex: 1.3 }]}>Due Date</Text>
            <Text style={[styles.th, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>Total</Text>
            <Text style={[styles.th, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>Paid</Text>
            <Text style={[styles.th, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>Balance</Text>
          </View>
          {invoices.length === 0 ? (
            <View style={styles.trLast}>
              <Text style={[styles.td, { flex: 7.6 }]}>No open invoices.</Text>
            </View>
          ) : (
            invoices.map((inv, i) => {
              const last = i === invoices.length - 1
              return (
                <View key={i} style={last ? styles.trLast : styles.tr}>
                  <Text style={[styles.td, { flex: 2 }]}>{inv.invoiceNumber}</Text>
                  <Text style={[styles.td, styles.cellBorderLeft, { flex: 1.3 }]}>{formatDate(inv.invoiceDate)}</Text>
                  <Text style={[styles.td, styles.cellBorderLeft, { flex: 1.3 }]}>{formatDate(inv.dueDate)}</Text>
                  <Text style={[styles.td, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>{money(inv.total)}</Text>
                  <Text style={[styles.td, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>{money(inv.amountPaid)}</Text>
                  <Text style={[styles.td, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>{money(inv.balanceDue)}</Text>
                </View>
              )
            })
          )}
        </View>

        <View style={styles.totalsBlock}>
          <View style={styles.grandTotalRow}>
            <Text style={[styles.totalsLabel, { fontFamily: 'Times-Bold' }]}>Total Balance Due</Text>
            <Text style={[styles.totalsValue, { fontSize: 12 }]}>{money(totalBalanceDue)}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Payment History</Text>
        <View style={styles.table}>
          <View style={styles.tr}>
            <Text style={[styles.th, { flex: 1.3 }]}>Date</Text>
            <Text style={[styles.th, styles.cellBorderLeft, { flex: 2 }]}>Invoice #</Text>
            <Text style={[styles.th, styles.cellBorderLeft, { flex: 1.5 }]}>Method</Text>
            <Text style={[styles.th, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>Amount</Text>
          </View>
          {payments.length === 0 ? (
            <View style={styles.trLast}>
              <Text style={[styles.td, { flex: 5.8 }]}>No payments on file.</Text>
            </View>
          ) : (
            payments.map((p, i) => {
              const last = i === payments.length - 1
              return (
                <View key={i} style={last ? styles.trLast : styles.tr}>
                  <Text style={[styles.td, { flex: 1.3 }]}>{formatDate(p.paymentDate)}</Text>
                  <Text style={[styles.td, styles.cellBorderLeft, { flex: 2 }]}>{p.invoiceNumber}</Text>
                  <Text style={[styles.td, styles.cellBorderLeft, { flex: 1.5 }]}>{p.method}</Text>
                  <Text style={[styles.td, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>{money(p.amount)}</Text>
                </View>
              )
            })
          )}
        </View>

        <Text
          style={styles.footer}
          render={() => `${RBC_BRANDING.companyName} · ${RBC_BRANDING.companyAddress} · ${RBC_BRANDING.repPhone}`}
        />
      </Page>
    </Document>
  )
}

export default CustomerStatementPdf
